import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, TrendingUp, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Earnings = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    // Wait for user context to load
    if (user === null) {
      // Still loading
      return;
    }
    
    setAuthLoading(false);
    
    if (!user) {
      // No user after loading - redirect to auth
      navigate('/auth');
      return;
    }
    
    fetchEarnings();
    fetchStripeStatus();

    // Check if returning from Stripe onboarding
    if (searchParams.get('onboarding') === 'success') {
      toast.success('Stripe Connect setup completed! You can now receive payouts.');
      fetchStripeStatus(); // Refresh status
    }
  }, [user, navigate, searchParams]);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/sales`);
      setSales(response.data.sales);
      setTotalEarnings(response.data.total_earnings);
      setSalesCount(response.data.sales_count);
    } catch (error) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/account-status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    }
  };

  const handleStartOnboarding = async () => {
    setLoadingStripe(true);
    try {
      const response = await axios.post(`${API}/stripe/connect/onboarding-link`);
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start Stripe onboarding');
      setLoadingStripe(false);
    }
  };

  const handleAccessDashboard = async () => {
    setLoadingStripe(true);
    try {
      const response = await axios.post(`${API}/stripe/connect/dashboard-link`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error('Failed to access Stripe dashboard');
    } finally {
      setLoadingStripe(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="mb-6 rounded-xl"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Home
      </Button>

      <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
        Your Earnings
      </h1>
      <p className="text-gray-600 mb-8">Track your sales and manage payouts</p>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-100 text-sm font-medium">Total Earnings</span>
            <DollarSign className="w-8 h-8 text-blue-200" />
          </div>
          <p className="text-4xl font-bold">${totalEarnings.toFixed(2)}</p>
          <p className="text-blue-100 text-sm mt-1">Your 85% share</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Sales</span>
            <TrendingUp className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-4xl font-bold text-gray-900">{salesCount}</p>
          <p className="text-gray-500 text-sm mt-1">Documents sold</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Average Sale</span>
            <DollarSign className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-4xl font-bold text-gray-900">
            ${salesCount > 0 ? (totalEarnings / salesCount).toFixed(2) : '0.00'}
          </p>
          <p className="text-gray-500 text-sm mt-1">Per document</p>
        </div>
      </div>

      {/* Stripe Connect Status */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
          Payout Setup
        </h2>

        {stripeStatus && !stripeStatus.onboarding_complete && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Stripe Connect setup required</p>
                <p className="text-sm text-amber-700 mt-1">
                  To receive payouts for your sales, you need to complete Stripe Connect onboarding. 
                  This is a secure process managed by Stripe.
                </p>
              </div>
            </div>
          </div>
        )}

        {stripeStatus && stripeStatus.onboarding_complete && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Payout account connected</p>
                <p className="text-sm text-green-700 mt-1">
                  Your Stripe Connect account is active. Earnings are automatically transferred to your account.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          {stripeStatus && !stripeStatus.onboarding_complete ? (
            <Button
              onClick={handleStartOnboarding}
              disabled={loadingStripe}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6 py-3"
            >
              {loadingStripe ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Complete Stripe Setup
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleAccessDashboard}
              disabled={loadingStripe}
              variant="outline"
              className="rounded-xl px-6 py-3"
            >
              {loadingStripe ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Stripe Dashboard
                </>
              )}
            </Button>
          )}
        </div>

        <p className="text-sm text-gray-500 mt-4">
          Stripe handles all tax forms, identity verification, and payout processing securely.
        </p>
      </div>

      {/* Sales History */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
          Sales History
        </h2>

        {sales.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No sales yet</p>
            <Button
              onClick={() => navigate('/upload')}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              Upload Your First Document
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Document</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Buyer</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Date</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium">Your Earnings</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <p className="font-medium text-gray-900">{sale.document_title}</p>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {sale.buyer_id.substring(0, 8)}...
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {new Date(sale.purchase_date).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-semibold text-green-600">
                        ${sale.seller_earnings.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Earnings;
